import { useSyncExternalStore } from 'react';

/**
 * Hiệu ứng vào / ra của thẻ việc trên board — APM-BOARD-fx.
 *
 * Hai sự kiện xảy ra **ngoài** board nhưng phải hiện **trên** board:
 *
 * - **Tạo việc** (`TaskFormDialog`, mở từ board hay từ thanh công cụ): thẻ mới
 *   chỉ có mặt sau khi dữ liệu tải lại. Nơi tạo `announceCreated(id)`; thẻ nào
 *   mọc ra mang đúng id ấy thì tự cuộn tới và chạy hiệu ứng đáp xuống
 *   (`.board-land`), rồi xoá mình khỏi danh sách chờ.
 * - **Xoá việc** (`DeleteTaskButton` trong ngăn chi tiết): `playLeave(id, done)`
 *   cho thẻ thu nhỏ – mờ – co lại (`.board-leave`) **trước**, rồi mới gọi
 *   `done()` để làm mới dữ liệu. Làm mới ngay thì thẻ biến mất trong một khung
 *   hình, và không có gì để nhìn.
 *
 * Một kho nhỏ ngoài React thay vì context: bên tạo (hộp thoại) và bên hiện (thẻ)
 * không chung cây con nào gần nhau, và mỗi thẻ chỉ nghe **đúng id của mình**
 * (`useCardMotion`) — đổi trạng thái một thẻ không vẽ lại cả board.
 *
 * ⚠️ Hẹn giờ bằng `setTimeout`, không đợi `animationend`: ở
 * `prefers-reduced-motion` thì `animation: none` và sự kiện ấy không bao giờ bắn
 * — thẻ đang "biến mất" sẽ nằm lại mãi.
 */

/**
 * Thời lượng hiệu ứng xoá, ms. ⚠️ Khớp `animation-duration` của `.board-leave`
 * trong `styles/index.css`. Hằng riêng, không dùng lại `LAND_MS` của board: hai
 * hiệu ứng trùng thời lượng hôm nay là do người dùng chọn, không phải một luật.
 */
export const LEAVE_MS = 1_500;

/** Thời lượng hiệu ứng tạo mới, ms — cùng `.board-land` (1,5s). */
export const CREATE_MS = 1_500;

/**
 * Chờ thẻ mới mọc ra tối đa bấy nhiêu ms. Tạo từ màn hình không có board (Danh
 * sách, Gantt) thì không thẻ nào nhận; bỏ đi, để lần mở board sau đó không bất
 * ngờ nở ra một thẻ tạo từ mười phút trước.
 */
const CREATED_WAIT_MS = 5_000;

export type CardMotion = 'created' | 'leaving' | null;

const created = new Set<string>();
const leaving = new Set<string>();
const listeners = new Set<() => void>();
/** Ảnh chụp bất biến của `leaving` cho `useLeavingIds` — đổi tham chiếu chỉ khi tập đổi. */
let leavingSnapshot: ReadonlySet<string> = new Set();

function emit(): void {
  if (
    leavingSnapshot.size !== leaving.size ||
    [...leaving].some((id) => !leavingSnapshot.has(id))
  ) {
    leavingSnapshot = new Set(leaving);
  }
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Việc vừa tạo — thẻ của nó sẽ cuộn tới và đáp xuống khi mọc ra. */
export function announceCreated(taskId: string): void {
  created.add(taskId);
  emit();
  setTimeout(() => {
    if (created.delete(taskId)) emit();
  }, CREATED_WAIT_MS);
}

/** Thẻ gọi khi đã nhận hiệu ứng tạo mới — không chạy lần hai. */
export function takeCreated(taskId: string): boolean {
  const had = created.delete(taskId);
  if (had) emit();
  return had;
}

/**
 * Cho thẻ chạy hiệu ứng biến mất rồi gọi `done`. Không có thẻ nào đang hiện
 * (xoá từ Danh sách, Gantt…) thì gọi `done` ngay — không bắt người dùng chờ
 * một hiệu ứng không ai thấy.
 */
export function playLeave(taskId: string, done: () => void): void {
  if (!document.querySelector(`[data-board-card="${CSS.escape(taskId)}"]`)) {
    done();
    return;
  }
  leaving.add(taskId);
  emit();
  setTimeout(() => {
    done();
    /*
      Cờ ở lại **sau** `done()`: dữ liệu mới còn đang trên đường về, và gỡ cờ
      ngay thì thẻ (đã co về 0, `animation-fill-mode: forwards`) hiện lại đủ
      một nhịp mạng trước khi biến mất thật. Tới lúc cờ đi, thẻ đã không còn.
    */
    setTimeout(() => {
      if (leaving.delete(taskId)) emit();
    }, CREATED_WAIT_MS);
  }, LEAVE_MS);
}

/**
 * Những thẻ đang biến mất — cột dùng để **giữ lại** thẻ ở chỗ cũ khi dữ liệu mới
 * đã bỏ nó đi.
 *
 * ⚠️ Vì sao cần: xoá việc làm server phát `task.changed`, và
 * `useNotificationStream` làm mới `['board']` **ngay** — dữ liệu về trong vài
 * trăm ms, sớm hơn nhiều so với `LEAVE_MS`. Không giữ lại thì thẻ rời DOM khi
 * hiệu ứng mới chạy được một phần nhỏ, và người dùng không thấy gì (2026-09-28,
 * dự án DWH).
 */
export function useLeavingIds(): ReadonlySet<string> {
  return useSyncExternalStore(
    subscribe,
    () => leavingSnapshot,
    () => leavingSnapshot,
  );
}

/** Trạng thái hiệu ứng của **một** thẻ. */
export function useCardMotion(taskId: string): CardMotion {
  return useSyncExternalStore(
    subscribe,
    () => (leaving.has(taskId) ? 'leaving' : created.has(taskId) ? 'created' : null),
    () => null,
  );
}
